import { Component, ComponentFactoryResolver, ElementRef, Injector, OnInit, ViewChild } from '@angular/core';
import * as d3 from 'd3';
import { Subscription } from 'rxjs';
import { MBTI } from 'src/app/interfaces/mbti';
import { WebviewService } from 'src/app/services/webview/webview.service';
import { BubbleContentComponent } from './components/bubble-content/bubble-content.component';

type MbtiProportionData = {
  mbti: MBTI
  count: number
}
type BubbleChartCircleData = MbtiProportionData & {
  level: number
  color: string
}

@Component({
  selector: 'app-bubble-chart',
  template: '<div #chart></div>',
  styles: [],
})
export class BubbleChartComponent implements OnInit {
  @ViewChild('chart', { static: true }) chartContainer: ElementRef | undefined;

  
  constructor(private webviewService: WebviewService, private componentFactoryResolver: ComponentFactoryResolver, private injector: Injector) { }
  
  circleLevel = 5;
  windowWidth = 350;
  private data: BubbleChartCircleData[] = []
  private webViewParamSubscription: Subscription | undefined;
  
  ngOnInit() {
    this.webViewParamSubscription = this.webviewService.getInitParamSubject().subscribe((params) => {
      if (params && this.data.length === 0) {
        this.data = params.data ?? [];
        this.windowWidth = params.width ?? this.windowWidth;
        this.renderBubbleChart();
      }
    })
  }

  ngOnDestroy() { 
    this.webViewParamSubscription?.unsubscribe();
  }

  private renderBubbleChart(): void {
    if (!this.chartContainer?.nativeElement) {
      return
    }
    const data: BubbleChartCircleData[] = this.webviewService.initParam?.data;
    if (!data) {
      return;
    }
    const padding = 3;
    const width = this.windowWidth;
    const height = this.windowWidth;
  
    const svg = d3
    .select(this.chartContainer.nativeElement)
    .append('svg')
    .attr('width', width)
    .attr('height', height);

    const root = d3
      .hierarchy({ children: data })
      .sum((d) => Math.pow(d.level, 2))

    const pack = d3
      .pack()
      .size([width, height])
      .padding(padding);

    pack(root);

    const nodes = root.descendants().slice(1);

    const bubbles = svg
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr('transform', (d) => `translate(${d.x}, ${d.y})`);

    bubbles
      .append('circle')
      .attr('r', (d) => d.r)
      .style('fill', (d) => d.data.color);

    nodes.forEach((node) => {
      this.createBubbleContentComponent(node);
    });
  }

  private createBubbleContentComponent(node: any): void {
    const factory = this.componentFactoryResolver.resolveComponentFactory(BubbleContentComponent);
    const componentRef = factory.create(this.injector);
    const nativeElement = componentRef.location.nativeElement;

    nativeElement.style.position = 'absolute';
    nativeElement.style.left = `${node.x - node.r}px`;
    nativeElement.style.top = `${node.y - node.r}px`;
    nativeElement.style.width = `${node.r}px`;

    componentRef.instance.mbti = node.data.mbti;
    componentRef.instance.count = node.data.count;
    componentRef.instance.level = node.data.level;
    componentRef.instance.radius = node.r;
    
    componentRef.changeDetectorRef.detectChanges();

    this.chartContainer?.nativeElement.appendChild(nativeElement);
  }
}
